import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { LocalizationProvider } from "@mui/x-date-pickers";
import DatePicker from "../../Components/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import MUIDataTable from "mui-datatables";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import StatusAlert from "../../StatusAlertList/StatusAlert";
import { FastTagApi } from "../../Data/axios";
import config from "../../Data/AuthUser";

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr.replace(" ", "T"));
  if (isNaN(date)) return dateStr;
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr.replace(" ", "T"));
  if (isNaN(date)) return dateStr;
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
};

const DriverAvatar = ({ username, userImage }) => {
  const [imageError, setImageError] = useState(false);
  const getInitials = (name) => {
    if (!name) return "NA";
    const words = name.trim().split(" ");
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  };
  const imageUrl =
    userImage && userImage !== "non" && !imageError
      ? `${config.apiImageUrl}${userImage}`
      : null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <Avatar
        src={imageUrl || undefined}
        alt={username}
        onError={() => setImageError(true)}
        sx={{
          width: 32,
          height: 32,
          backgroundColor: "#1976d2",
          color: "white",
          fontWeight: "bold",
          fontSize: "12px",
        }}
      >
        {getInitials(username)}
      </Avatar>
      <Typography variant="body2" fontWeight="bold" color="black">
        {username || "N/A"}
      </Typography>
    </div>
  );
};

const COLORS = ["#1976d2", "#388e3c", "#ed6c02", "#7b1fa2", "#c62828", "#00838f", "#5d4037", "#2e7d32", "#f9a825", "#5e35b1"];

const FastTag = () => {
  const [summary, setSummary] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [plazaSearch, setPlazaSearch] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedPlaza, setSelectedPlaza] = useState(null);
  const [detailData, setDetailData] = useState(null);

  const showAlert = (message, severity) => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const buildParams = (override = {}) => {
    const params = {};
    if (fromDate && toDate) {
      params.start_date = dayjs(fromDate).format("YYYY-MM-DD");
      params.end_date = dayjs(toDate).format("YYYY-MM-DD");
    } else if (fromDate) {
      params.date = dayjs(fromDate).format("YYYY-MM-DD");
    }
    if (plazaSearch) params.plaza = plazaSearch;
    return { ...params, ...override };
  };

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await FastTagApi.getFastagSummary(buildParams());
      if (res.success) {
        setSummary(res.data || []);
        setTotals(res.totals || {});
      } else {
        showAlert(res.message || "Failed to fetch FASTAG summary", "error");
      }
    } catch (err) {
      showAlert("An error occurred while fetching FASTAG summary", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  const handleApply = () => {
    if (fromDate && toDate && dayjs(fromDate).isAfter(dayjs(toDate))) {
      showAlert("From date cannot be after To date", "warning");
      return;
    }
    fetchSummary();
  };

  const handleClear = () => {
    setFromDate(null);
    setToDate(null);
    setPlazaSearch("");
    setTimeout(() => fetchSummary(), 0);
  };

  const openPlazaDetails = async (plazaName) => {
    setSelectedPlaza(plazaName);
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailData(null);
    try {
      const res = await FastTagApi.getFastagPlazaDetails(
        buildParams({ plaza_name: plazaName })
      );
      if (res.success) {
        setDetailData(res.data);
      } else {
        showAlert(res.message || "Failed to fetch plaza details", "error");
      }
    } catch (err) {
      showAlert("An error occurred while fetching plaza details", "error");
    } finally {
      setDetailLoading(false);
    }
  };

  const chartData = summary
    .slice(0, 15)
    .map((item, idx) => ({
      name: item.toll_plaza_name || "Unknown",
      records: parseInt(item.total_records) || 0,
      drivers: parseInt(item.total_drivers) || 0,
      color: COLORS[idx % COLORS.length], // Random colors for bars as requested
    }));

  const columns = [
    {
      name: "toll_plaza_name",
      label: "Toll Plaza",
      options: {
        customBodyRender: (value) => (
          <Typography variant="body2" fontWeight="bold" color="black">
            {value || "N/A"}
          </Typography>
        ),
        setCellProps: () => ({ style: { minWidth: "220px" } }),
      },
    },
    {
      name: "total_records",
      label: "Total Records",
      options: {
        customBodyRender: (value) => (
          <Typography variant="body2" fontWeight="bold" color="black">
            {parseInt(value) || 0}
          </Typography>
        ),
        setCellProps: () => ({ style: { textAlign: "center" } }),
      },
    },
    {
      name: "total_drivers",
      label: "Drivers Passed",
      options: {
        customBodyRender: (value) => (
          <Typography variant="body2" fontWeight="bold" color="black">
            {parseInt(value) || 0}
          </Typography>
        ),
        setCellProps: () => ({ style: { textAlign: "center" } }),
      },
    },
    {
      name: "last_passed_at",
      label: "Last Passed",
      options: {
        customBodyRender: (value) => (
          <Typography variant="body2" color="black">
            {formatDateTime(value)}
          </Typography>
        ),
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        customBodyRender: (value, tableMeta) => (
          <Button
            variant="contained"
            size="small"
            onClick={() =>
              openPlazaDetails(summary[tableMeta.rowIndex]?.toll_plaza_name)
            }
            sx={{
              backgroundColor: "#1976d2",
              "&:hover": { backgroundColor: "#1565c0" },
              textTransform: "none",
            }}
          >
            View Drivers
          </Button>
        ),
        setCellProps: () => ({ style: { textAlign: "center" } }),
      },
    },
  ];

  const options = {
    selectableRows: "none",
    filter: false,
    viewColumns: false,
    search: false,
    download: false,
    print: false,
    elevation: 0,
    responsive: "standard",
    rowsPerPageOptions: [10, 25, 50, 100],
    rowsPerPage: 10,
    onRowClick: (rowData, rowMeta) => {
      const plaza = summary[rowMeta.dataIndex];
      if (plaza?.toll_plaza_name) openPlazaDetails(plaza.toll_plaza_name);
    },
    textLabels: {
      body: {
        noMatch: loading ? "Loading FASTAG data..." : "No FASTAG records found",
      },
    },
  };

  const summaryCards = [
    { label: "Total Toll Records", value: parseInt(totals.total_records) || 0, color: "#1976d2" },
    { label: "Total Drivers Passed", value: parseInt(totals.total_drivers) || 0, color: "#388e3c" },
    { label: "Total Vehicles", value: parseInt(totals.total_vehicles) || 0, color: "#ed6c02" },
    { label: "Toll Plazas", value: summary.length, color: "#7b1fa2" },
  ];

  return (
    <>
      <StatusAlert
        open={alertOpen}
        setOpen={setAlertOpen}
        message={alertMessage}
        severity={alertSeverity}
      />

      <Box sx={{ background: "white", borderBottom: "1px solid #e0e0e0", padding: "20px 0", marginBottom: "24px" }}>
        <Box sx={{ maxWidth: "100%", margin: "0 auto", padding: "0 24px" }}>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#000" }}>
                FASTag Records
              </Typography>
              <Typography variant="body2" sx={{ color: "#000" }}>
                Toll plaza transactions from active & completed trips
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" fontWeight={600} color="#000" textTransform="uppercase" display="block" mb={1}>
                From Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      sx: {
                        '& .MuiInputBase-input': { color: 'black' },
                        '& .MuiInputLabel-root': { color: 'black' },
                        '& .MuiIconButton-root': { color: 'black' }, // Calendar icon to black
                        '& .MuiInputAdornment-root .MuiIconButton-root': { color: 'black' }, // Ensure calendar icon is black
                      },
                    },
                    openPickerButton: {
                      sx: { color: 'black' } // Calendar icon to black
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" fontWeight={600} color="#000" textTransform="uppercase" display="block" mb={1}>
                To Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      sx: {
                        '& .MuiInputBase-input': { color: 'black' },
                        '& .MuiInputLabel-root': { color: 'black' },
                        '& .MuiIconButton-root': { color: 'black' }, // Calendar icon to black
                        '& .MuiInputAdornment-root .MuiIconButton-root': { color: 'black' }, // Ensure calendar icon is black
                      },
                    },
                    openPickerButton: {
                      sx: { color: 'black' } // Calendar icon to black
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" fontWeight={600} color="#000" textTransform="uppercase" display="block" mb={1}>
                Search Plaza
              </Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="e.g., K N Huindy TOLL PLAZA"
                value={plazaSearch}
                onChange={(e) => setPlazaSearch(e.target.value)}
                InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, fontSize: 18, color: "#000" }} /> }}
                sx={{ '& .MuiInputBase-input': { color: 'black' }, '& .MuiInputBase-input::placeholder': { color: 'black' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleApply}
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={16} sx={{ color: "white" }} />}
                  sx={{ backgroundColor: "#2196f3", "&:hover": { backgroundColor: "#1976d2" } }}
                >
                  {loading ? "Loading..." : "Apply"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClear}
                  startIcon={<ClearIcon />}
                  sx={{ borderColor: "black", color: "black" }}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box sx={{ padding: "0 24px 24px 24px" }}>
        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {summaryCards.map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.label}>
              <Card sx={{ backgroundColor: "#fff", borderRadius: "10px", boxShadow: "0px 2px 8px rgba(0,0,0,0.08)" }}>
                <CardContent>
                  <Typography variant="h4" fontWeight={700} sx={{ color: card.color }}>
                    {card.value}
                  </Typography>
                  <Typography variant="body2" color="#000" fontWeight={600}>
                    {card.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Chart */}
        {chartData.length > 0 && (
          <Card sx={{ mb: 3, borderRadius: "10px", boxShadow: "0px 2px 8px rgba(0,0,0,0.08)", backgroundColor: "#fff" }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" color="black" sx={{ mb: 2 }}>
                Toll Records by Plaza
              </Typography>
              <Box sx={{ backgroundColor: "#fff", borderRadius: "8px", width: "100%" }}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: "#000", fontSize: 10 }}
                      interval={0}
                      angle={-35}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis tick={{ fill: "#000", fontSize: 12 }} />
                    <Tooltip cursor={{ fill: "rgba(0,0,0,0.05)" }} contentStyle={{ backgroundColor: "#fff", color: "#000", borderRadius: 8 }} />
                    <Bar dataKey="records" name="Records" fill="#1976d2" radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Summary Table */}
        <Box
          sx={{
            background: "white",
            borderRadius: "10px",
            boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
            "& .MuiPaper-root": { backgroundColor: "white", color: "black" },
            "& .MuiTableCell-root": { color: "black", backgroundColor: "white" },
            "& .MuiTableCell-head": { color: "black", fontWeight: 700 },
            "& .MuiTableBody-root .MuiTableRow-root:hover": { backgroundColor: "#fafafa" },
            "& .MuiTablePagination-root": { color: "black" },
          }}
        >
          <MUIDataTable
            title={
              <Typography variant="h6" fontWeight="bold" color="black">
                Toll Plaza Summary
              </Typography>
            }
            data={summary}
            columns={columns}
            options={options}
          />
        </Box>
      </Box>

      {/* Plaza Details Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { backgroundColor: "white", color: "black" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "1px solid #e0e0e0", display: "flex", justifyContent: "space-between", alignItems: "center", color: "black" }}>
          <Box>
            Drivers Passed - {selectedPlaza || "Plaza"}
            {detailData && (
              <Typography variant="caption" display="block" color="black" fontWeight={500}>
                {detailData.total_records} records • {detailData.total_drivers} drivers
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setDetailOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px", flexDirection: "column", gap: 2 }}>
              <CircularProgress />
              <Typography variant="body2" color="black">Loading plaza details...</Typography>
            </Box>
          ) : detailData?.drivers?.length > 0 ? (
            <Grid container spacing={2}>
              {detailData.drivers.map((driver) => (
                <Grid item xs={12} sm={6} key={driver.driver_id}>
                  <Card sx={{ backgroundColor: "#ffffff", borderRadius: "8px", border: "1px solid #e0e0e0" }}>
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <DriverAvatar username={driver.driver_name} userImage={driver.driver_image} />
                        <Chip
                          label={`${driver.total_passes} passes`}
                          size="small"
                          sx={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 }}
                        />
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      {driver.transactions?.map((txn, idx) => (
                        <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                          <Typography variant="body2" color="black">
                            {txn.vehicle_reg_no} • Lane {txn.lane_direction}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} color="black">
                            {formatDateTime(txn.reader_read_time)}
                          </Typography>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "150px" }}>
              <Typography variant="body1" color="black">
                No drivers found for this plaza in the selected period.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)} sx={{ color: "black" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FastTag;
